import { phpAuthUser } from "../../core/php-data-access";
import { UserFollowApi } from "./user-follow-api";
import { UserFollowRender } from "./user-follow-render";
import { userFollowForm } from "./user-follow-form";
import { UserFollowListener } from "./user-follow-listener";
import { translator } from "../../translation/translator";
import { Roles } from "../../constants/roles";
import type { PhpAuthUser } from "../../types/php-injected";
import { toPositiveIntOrNull } from "../../util/id-utils";

type AuthUserLike = PhpAuthUser;

export class UserFollowHandler {
    public container: HTMLElement;

    public targetUserId: number;

    public button: HTMLButtonElement | null;

    public label: HTMLElement | null;

    public followersCountEl: HTMLElement | null;

    public followersListEl: HTMLElement | null;

    public followingsCountEl: HTMLElement | null;

    public followingsListEl: HTMLElement | null;

    public localFollowersValueEl: HTMLElement | null;

    public authUser: AuthUserLike | null;

    public isFollowing: boolean;

    public targetFollowsViewer: boolean;

    public followLabelMode: string;

    public currentProfileUserId: number;

    public authUserId: number;

    public syncAuthUserFollowings(nextFollowing: boolean): void {
        if (!this.authUser) return;

        const current = Array.isArray(this.authUser.followings)
            ? this.authUser.followings.map((id) => Number(id || 0)).filter((id) => Number.isFinite(id) && id > 0)
            : [];

        const next = nextFollowing
            ? Array.from(new Set([...current, this.targetUserId]))
            : current.filter((id) => id !== this.targetUserId);

        this.authUser.followings = next;

        const globalWindow = window as Window & { authUser?: AuthUserLike | null };
        if (globalWindow.authUser) {
            globalWindow.authUser.followings = next;
        }
    }

    public syncTargetProfileFollowers(nextFollowing: boolean): void {
        if (this.currentProfileUserId !== this.targetUserId) {
            return;
        }

        if (this.followersCountEl instanceof HTMLElement) {
            UserFollowRender.updateFollowersCount(this.followersCountEl, nextFollowing ? 1 : -1);
        }

        if (!(this.followersListEl instanceof HTMLElement) || !this.authUser) {
            return;
        }

        const viewerUserId = Number(this.authUser.user_id || this.authUser.id || 0);
        if (!Number.isFinite(viewerUserId) || viewerUserId <= 0) {
            return;
        }

        if (nextFollowing) {
            UserFollowRender.addFollowerToModalList(this.followersListEl, this.authUser);
            return;
        }

        UserFollowRender.removeFollowerFromModalList(this.followersListEl, viewerUserId);
    }

    public syncOwnProfileFollowings(nextFollowing: boolean): void {
        if (this.currentProfileUserId !== this.authUserId || this.authUserId <= 0) {
            return;
        }

        if (this.followingsCountEl instanceof HTMLElement) {
            UserFollowRender.updateFollowersCount(this.followingsCountEl, nextFollowing ? 1 : -1);
        }

        if (!(this.followingsListEl instanceof HTMLElement)) {
            return;
        }

        const existingCard = userFollowForm.getFollowingCard(this.followingsListEl, this.targetUserId);

        if (nextFollowing) {
            if (existingCard instanceof HTMLElement) {
                return;
            }

            const sourceSlide = userFollowForm.getSourceSlide(this.container);
            if (!(sourceSlide instanceof HTMLElement)) {
                return;
            }

            const clone = sourceSlide.cloneNode(true);
            if (!(clone instanceof HTMLElement)) {
                return;
            }

            clone.classList.add("user-following-card");
            clone.dataset.role = Roles.USER_PROFILE_FOLLOWING_CARD;
            clone.dataset.followingUserId = String(this.targetUserId);

            const clonedFollowContainer = userFollowForm.getFollowContainer(clone);
            if (clonedFollowContainer instanceof HTMLElement) {
                delete clonedFollowContainer.dataset.userFollowInitialized;
                clonedFollowContainer.setAttribute("data-initial-following", "1");
            }

            const emptyMessage = userFollowForm.getNoFollowingMessage(this.followingsListEl);
            if (emptyMessage instanceof HTMLElement) {
                emptyMessage.remove();
            }

            this.followingsListEl.prepend(clone);
            translator.updateContent();
            return;
        }

        if (existingCard instanceof HTMLElement) {
            existingCard.remove();
        }

        const hasFollowingCards = userFollowForm.getAnyFollowingCard(this.followingsListEl) instanceof HTMLElement;
        if (!hasFollowingCards) {
            this.followingsListEl.innerHTML =
                '<p class="text-muted text-center" data-translate="no_following">ยังไม่ได้ติดตามใคร</p>';
            translator.updateContent();
        }
    }

    public constructor(container: HTMLElement) {
        this.container = container;
        this.targetUserId = toPositiveIntOrNull(container.getAttribute("data-target-user-id") || undefined) ?? 0;
        this.button = userFollowForm.getToggleButton(container);
        this.label = userFollowForm.getToggleLabel(container);
        this.authUser = phpAuthUser() as AuthUserLike | null;
        this.authUserId = Number(this.authUser?.user_id || this.authUser?.id || 0);
        const profilePanelsRoot = userFollowForm.getProfilePanelsRoot(document);
        this.currentProfileUserId = toPositiveIntOrNull(profilePanelsRoot?.dataset.userId) ?? 0;
        this.followersCountEl =
            this.currentProfileUserId === this.targetUserId ? userFollowForm.getFollowersCount(document) : null;
        this.followersListEl =
            this.currentProfileUserId === this.targetUserId ? userFollowForm.getFollowersList(document) : null;
        this.followingsCountEl =
            this.currentProfileUserId === this.authUserId ? userFollowForm.getFollowingsCount(document) : null;
        this.followingsListEl =
            this.currentProfileUserId === this.authUserId ? userFollowForm.getFollowingsList(document) : null;
        this.localFollowersValueEl = userFollowForm.getFollowerFollowersValue(container);
        this.isFollowing = container.getAttribute("data-initial-following") === "1";
        this.targetFollowsViewer = container.getAttribute("data-target-follows-viewer") === "1";
        this.followLabelMode = container.getAttribute("data-follow-label-mode") || "basic";
    }

    public init(): void {
        if (!Number.isFinite(this.targetUserId) || this.targetUserId <= 0) return;
        if (!(this.button instanceof HTMLButtonElement) || !(this.label instanceof HTMLElement)) return;

        this.syncButtonUi();
        UserFollowListener.bindToggleClick(this.button, async () => {
            if (this.button instanceof HTMLButtonElement) {
                this.button.disabled = true;
            }
            try {
                if (this.isFollowing) {
                    await UserFollowApi.unfollow(this.targetUserId);
                    this.isFollowing = false;
                    this.syncAuthUserFollowings(false);
                    if (this.localFollowersValueEl instanceof HTMLElement) {
                        UserFollowRender.updateFollowersCount(this.localFollowersValueEl, -1);
                    }
                    this.syncTargetProfileFollowers(false);
                } else {
                    await UserFollowApi.follow(this.targetUserId);
                    this.isFollowing = true;
                    this.syncAuthUserFollowings(true);
                    if (this.localFollowersValueEl instanceof HTMLElement) {
                        UserFollowRender.updateFollowersCount(this.localFollowersValueEl, 1);
                    }
                    this.syncTargetProfileFollowers(true);
                }

                this.syncButtonUi();
                this.syncOwnProfileFollowings(this.isFollowing);
            } catch (error) {
                console.warn("[UserFollow] Toggle failed", error);
            } finally {
                if (this.button instanceof HTMLButtonElement) {
                    this.button.disabled = false;
                }
            }
        });
    }

    public syncButtonUi(): void {
        if (!(this.button instanceof HTMLButtonElement) || !(this.label instanceof HTMLElement)) return;
        const useReciprocalLabel = this.followLabelMode === "reciprocal";
        const translateKey = this.isFollowing
            ? "btn_unfollow"
            : useReciprocalLabel && this.targetFollowsViewer
              ? "btn_followback"
              : "btn_follow";
        const translatedLabel = translator.translate(translateKey);
        this.container.setAttribute("data-initial-following", this.isFollowing ? "1" : "0");
        this.button.setAttribute("aria-pressed", this.isFollowing ? "true" : "false");
        this.button.classList.toggle("is-following", this.isFollowing);
        this.button.classList.toggle("unfollow", this.isFollowing);
        this.button.classList.toggle("followback", !this.isFollowing && useReciprocalLabel && this.targetFollowsViewer);
        this.button.classList.toggle("follow", !this.isFollowing && (!useReciprocalLabel || !this.targetFollowsViewer));
        this.button.title = translatedLabel;
        this.label.setAttribute("data-translate", translateKey);
        this.label.textContent = translatedLabel;
    }
}
